import { Dispatch, SetStateAction, useCallback, useMemo, useRef } from 'react';

import useReceivers from '../../../../fetchHooks/useReceivers';
import useTranslation, { format } from '../../../../hooks/useTranslation';
import usePartialReceiverOcr from '../../../transaction/Payment/fields/usePartialReceiverOcr';
import { SelectedReceiver } from '../../../transaction/SelectedReceiver';

function useReceiverSelector(
    toAccountState: { value: SelectedReceiver | undefined; error: string | undefined },
    curryChange: (selectedReceiver: SelectedReceiver | undefined) => void,
    setSearchString: Dispatch<SetStateAction<string>>,
    locked = false,
) {
    const curry = useRef(curryChange);

    const translated = useTranslation();
    const waitingForReceiver = locked && !toAccountState;
    const { data: receivers, ...receiverQuery } = useReceivers();
    const waitingForReceivers = receiverQuery.isLoading || waitingForReceiver;

    const resetSearchString = useCallback(() => {
        setSearchString('');
    }, [setSearchString]);

    const { ocr, selectReceiver, invalidAccountNumber } = usePartialReceiverOcr(curry.current, resetSearchString);

    const toAccountErrorMessage = useMemo(() => {
        if (!ocr.error && !toAccountState.error && !invalidAccountNumber) {
            return undefined;
        }
        const isOcrError = !!ocr.error || !!invalidAccountNumber;
        const message = isOcrError
            ? format(translated.invalidToAccount, invalidAccountNumber ?? ocr.variables?.accountNumber.value ?? '')
            : toAccountState.error;
        return {
            className: 'transaction-form__to-account-error',
            message: message ?? '',
        };
    }, [ocr.error, toAccountState.error, translated.invalidToAccount, ocr.variables, invalidAccountNumber]);

    return {
        toAccountErrorMessage,
        selectReceiver,
        ocr,
        receivers: receivers ?? [],
        waitingForReceivers,
        resetSearchString,
    };
}

export default useReceiverSelector;
